if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count. For the fly-to-cart effect the count is
          // updated when the flying image "lands" on the cart icon instead.
          const afterAtc = theme.settings.afterAtc;
          let flying = false;
          if (afterAtc === 'fly-to-cart' || afterAtc === 'fly-and-popup') {
            flying = this.flyToCart(data);
          }
          if (!flying) {
            ProductForm.updateCartIcon(data);
            if (afterAtc === 'popup' || afterAtc === 'fly-and-popup') ProductForm.showAddedPopup();
          }

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Animates a clone of the added product's image flying to the cart icon.
     * The cart count is updated when the animation lands. Falls back (returns
     * false) when there's no cart icon, no usable image, or reduced motion is
     * requested - the caller then updates the cart count immediately.
     * @param {object} data - The /cart/add.js response for the added line item.
     * @returns {boolean} True if the animation was started.
     */
    flyToCart(data) {
      const cartIcon = document.getElementById('cart-icon');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!cartIcon || reduceMotion) return false;

      // Prefer the on-screen product image nearest the button for visual
      // continuity, falling back to the image from the cart response.
      const sourceImg = this.closest('.card')?.querySelector('.card__main-image')
        || document.querySelector('#product-media img, .product-media img')
        || this.querySelector('img');
      const imageUrl = (sourceImg && (sourceImg.currentSrc || sourceImg.src))
        || data.featured_image?.url || data.image;
      if (!imageUrl) return false;

      const startRect = (sourceImg || this.submitBtn).getBoundingClientRect();
      if (!startRect.width || !startRect.height) return false;
      const endRect = cartIcon.getBoundingClientRect();

      const size = Math.min(startRect.width, 160);
      const startX = startRect.left + (startRect.width / 2) - (size / 2);
      const startY = startRect.top + (startRect.height / 2) - (size / 2);
      const endX = endRect.left + (endRect.width / 2) - (size / 2);
      const endY = endRect.top + (endRect.height / 2) - (size / 2);
      const dx = endX - startX;
      const dy = endY - startY;

      const clone = document.createElement('img');
      clone.src = imageUrl;
      clone.setAttribute('aria-hidden', 'true');
      Object.assign(clone.style, {
        position: 'fixed',
        left: `${startX}px`,
        top: `${startY}px`,
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'cover',
        borderRadius: '8px',
        zIndex: '1000',
        pointerEvents: 'none',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
        willChange: 'transform, opacity'
      });
      document.body.appendChild(clone);

      const animation = clone.animate([
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
        { transform: `translate(${dx * 0.5}px, ${(dy * 0.5) - 40}px) scale(0.7)`, opacity: 1, offset: 0.5 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.15)`, opacity: 0.4, offset: 1 }
      ], {
        duration: 450,
        easing: 'cubic-bezier(0.4, 0, 0.7, 0.5)',
        fill: 'forwards'
      });

      const land = () => {
        clone.remove();
        ProductForm.updateCartIcon(data);
        ProductForm.bumpCartIcon();
        // Combined option: reveal the popup as the image lands, so the two are in sync.
        if (theme.settings.afterAtc === 'fly-and-popup') ProductForm.showAddedPopup();
      };
      animation.onfinish = land;
      // Safety net in case onfinish never fires (e.g. tab backgrounded).
      setTimeout(() => {
        if (clone.isConnected) land();
      }, 800);

      return true;
    }

    /**
     * Gives the cart icon a brief pulse to acknowledge the landed item.
     */
    static bumpCartIcon() {
      const cartIcon = document.getElementById('cart-icon');
      if (!cartIcon || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      cartIcon.animate([
        { transform: 'scale(1)' },
        { transform: 'scale(1.25)' },
        { transform: 'scale(1)' }
      ], { duration: 300, easing: 'ease-out' });
    }

    /**
     * Briefly reveals the "Added to cart" popup near the cart icon.
     */
    static showAddedPopup() {
      const popup = document.getElementById('cart-added-popup');
      if (!popup) return;
      clearTimeout(ProductForm.popupTimer);
      popup.classList.add('is-visible');
      const visibleFor = parseInt(popup.dataset.visibleFor, 10) || 2000;
      ProductForm.popupTimer = setTimeout(() => {
        popup.classList.remove('is-visible');
      }, visibleFor);
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
